import { useEffect, useMemo, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  HomeModernIcon,
  Cog6ToothIcon,
  PencilSquareIcon,
  UsersIcon,
} from "@heroicons/react/24/outline";
import PageHeader from "../components/layout/PageHeader";
import { Card } from "../components/ui/Card";
import { Badge } from "../components/ui/Badge";
import { cabinsApi } from "../api/cabins";
import { campersApi } from "../api/campers";
import { CabinDetailModal } from "../components/cabins/CabinDetailModal";
import { useAuth } from "../hooks/useAuth";
import { SESSION_DISPLAY_LABEL, useSeason } from "../context/SeasonContext";
import { filterCamperCabins } from "../utils/cabinFilter";
import {
  DIVISION_RANGES,
  divisionForCabinNumber,
  formatCabinNumber,
  parseCabinNumber,
  type CampHighlandsDivision,
} from "../utils/terminology";
import {
  computeSessionTotals,
  SESSION_HEADER_LABEL,
  type SessionTotals,
} from "../utils/sessionTotals";
import { attributeCamperToDivision } from "../utils/divisionAttribution";
import toast from "react-hot-toast";
import type { Cabin, Camper } from "../types";

const STANDARD_DIVISION_ORDER: ReadonlyArray<Exclude<CampHighlandsDivision, "Rangers">> = [
  "Seniors",
  "Juniors",
  "Colts",
  "Cubs",
];

const HIDE_UNUSED_KEY = "acts_cabins_hide_unused";

interface CabinSlot {
  /** Cabin number 1-26 for standard slots. -1 for Rangers entries that have no number. */
  number: number;
  /** Display label (zero-padded number, or "Rangers"). */
  label: string;
  /** The Cabin row for this number in the active term, or null if absent (= "unused"). */
  cabin: Cabin | null;
}

interface DivisionBand {
  division: CampHighlandsDivision;
  slots: CabinSlot[];
}

/**
 * Group `Cabin[]` rows for the active term by physical cabin number, then
 * pour them into the fixed Camp Highlands division grid. Returns either the
 * 4 standard bands (Seniors/Juniors/Colts/Cubs) or a single Rangers band
 * depending on the active session.
 */
function buildBands(
  cabins: readonly Cabin[],
  rangersOnly: boolean,
): DivisionBand[] {
  const camperFacing = filterCamperCabins(cabins);

  if (rangersOnly) {
    const rangersCabins = camperFacing.filter(
      (c) => c.name != null && /^rangers$/i.test(String(c.name).trim()),
    );
    return [
      {
        division: "Rangers",
        slots: rangersCabins.map((cabin) => ({
          number: -1,
          label: "Rangers",
          cabin,
        })),
      },
    ];
  }

  // Index numbered cabins by their integer name. If sync somehow returned
  // duplicates for the same number (e.g. a stale row from a different term),
  // prefer the highest camper count so the card has data to show.
  const byNumber = new Map<number, Cabin>();
  for (const c of camperFacing) {
    const n = parseCabinNumber(c.name ?? "");
    if (n == null) continue;
    const existing = byNumber.get(n);
    if (!existing) {
      byNumber.set(n, c);
      continue;
    }
    const existingCount = existing.camperCount ?? 0;
    const candidateCount = c.camperCount ?? 0;
    if (candidateCount > existingCount) byNumber.set(n, c);
  }

  return STANDARD_DIVISION_ORDER.map((division) => {
    const [start, end] = DIVISION_RANGES[division];
    const slots: CabinSlot[] = [];
    for (let n = start; n <= end; n++) {
      slots.push({
        number: n,
        label: formatCabinNumber(String(n)),
        cabin: byNumber.get(n) ?? null,
      });
    }
    return { division, slots };
  });
}

export default function Cabins() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin" || user?.role === "director";
  const { activeSession, selectedTerm, selectedYear } = useSeason();

  const [cabins, setCabins] = useState<Cabin[]>([]);
  const [campers, setCampers] = useState<Camper[]>([]);
  const [loading, setLoading] = useState(true);
  const [cabinDetailOpen, setCabinDetailOpen] = useState(false);
  const [selectedCabin, setSelectedCabin] = useState<Cabin | null>(null);
  const [hideUnused, setHideUnused] = useState<boolean>(() => {
    const stored = localStorage.getItem(HIDE_UNUSED_KEY);
    return stored === "1";
  });

  const fetchCabins = useCallback(() => {
    setLoading(true);
    const params = selectedTerm ? { termId: selectedTerm.id } : { year: selectedYear };
    cabinsApi
      .list(params)
      .then((data) => setCabins(data))
      .catch(() => toast.error("Failed to load cabins"))
      .finally(() => setLoading(false));
  }, [selectedTerm, selectedYear]);

  // Fetch campers for the active term to compute Full Term subtotals per
  // division. The cabin list endpoint only returns aggregate camperCount;
  // isFullTerm lives on the camper rows and is what we group on here.
  useEffect(() => {
    if (!selectedTerm) {
      setCampers([]);
      return;
    }
    let cancelled = false;
    campersApi
      .list({ year: selectedTerm.year, termId: selectedTerm.id, pageSize: 500 })
      .then((res) => {
        if (!cancelled) setCampers(res.data);
      })
      .catch(() => {
        if (!cancelled) setCampers([]);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedTerm]);

  useEffect(() => {
    fetchCabins();
  }, [fetchCabins]);

  const toggleHideUnused = () => {
    setHideUnused((prev) => {
      const next = !prev;
      localStorage.setItem(HIDE_UNUSED_KEY, next ? "1" : "0");
      return next;
    });
  };

  const bands = useMemo(
    () => buildBands(cabins, activeSession === "rangers"),
    [cabins, activeSession],
  );

  // Per-division session totals (total / sessionOnly / fullTerm). Division
  // attribution comes from `attributeCamperToDivision` which prefers the
  // camper's own Division (Enrollment.division.name, ~ CampMinder's
  // CamperDetails.DivisionID) over the cabin's bunk-area attribution — so
  // Rangers campers count toward Rangers, not Seniors, even when their
  // physical bed is in cabin 04 (a Senior-range cabin) for 1st Term.
  const totalsByDivision = useMemo(() => {
    const map = new Map<CampHighlandsDivision, Camper[]>();
    for (const camper of campers) {
      const division = attributeCamperToDivision(camper, selectedTerm?.id, activeSession);
      if (!division) continue;
      const list = map.get(division) ?? [];
      list.push(camper);
      map.set(division, list);
    }
    const out = new Map<CampHighlandsDivision, SessionTotals>();
    for (const [div, list] of map.entries()) {
      out.set(div, computeSessionTotals(list));
    }
    return out;
  }, [campers, selectedTerm, activeSession]);

  const visibleBands = useMemo(() => {
    if (!hideUnused) return bands;
    return bands.map((band) => ({
      ...band,
      slots: band.slots.filter((s) => s.cabin != null),
    }));
  }, [bands, hideUnused]);

  const sessionLabel = SESSION_DISPLAY_LABEL[activeSession];
  const subtitle = selectedTerm
    ? `${selectedTerm.year} — ${sessionLabel}`
    : `${selectedYear} — ${sessionLabel}`;

  return (
    <div>
      <PageHeader
        title="Cabins"
        subtitle={subtitle}
        actions={
          isAdmin ? (
            <Link
              to="/admin/cabins"
              className="flex items-center gap-1.5 bg-white/20 hover:bg-white/30 text-white font-semibold text-sm px-3 py-2 rounded-lg shadow-sm transition-colors backdrop-blur-sm"
            >
              <Cog6ToothIcon className="w-4 h-4" />
              Manage Cabins
            </Link>
          ) : undefined
        }
      />

      <div className="p-6 lg:p-8 space-y-8">
        {isAdmin && activeSession !== "rangers" && (
          <div className="flex items-center justify-end">
            <label className="inline-flex items-center gap-2 text-xs text-stone-500 cursor-pointer select-none">
              <input
                type="checkbox"
                className="rounded border-stone-300 text-forest-600 focus:ring-forest-500"
                checked={hideUnused}
                onChange={toggleHideUnused}
              />
              Hide unused cabins
            </label>
          </div>
        )}

        {loading && cabins.length === 0 ? (
          <div className="space-y-6">
            {[1, 2].map((i) => (
              <div key={i} className="space-y-3">
                <div className="h-4 bg-stone-200 rounded w-1/4 animate-pulse" />
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {[1, 2, 3].map((j) => (
                    <div key={j} className="camp-card p-5 animate-pulse space-y-3">
                      <div className="h-4 bg-stone-200 rounded w-1/2" />
                      <div className="h-3 bg-stone-100 rounded w-1/3" />
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          visibleBands.map(({ division, slots }) => {
            const filled = slots.filter((s) => s.cabin != null).length;
            const subtotals = totalsByDivision.get(division);
            return (
              <div key={division}>
                <div className="flex items-baseline gap-3 mb-3 flex-wrap">
                  <h2 className="text-base font-semibold text-stone-700">{division}</h2>
                  {subtotals && subtotals.total > 0 && (
                    <span className="text-xs text-stone-500 font-medium">
                      {division === "Rangers"
                        ? `Rangers: ${subtotals.total}`
                        : `${subtotals.total} (${subtotals.sessionOnly} ${SESSION_HEADER_LABEL[activeSession].toLowerCase()} only · ${subtotals.fullTerm} full term)`}
                    </span>
                  )}
                  <span className="text-xs text-stone-400">
                    {filled} cabin{filled !== 1 ? "s" : ""}
                    {!hideUnused && division !== "Rangers" && filled !== slots.length && (
                      <span className="text-stone-300"> · {slots.length - filled} unused</span>
                    )}
                  </span>
                </div>

                {slots.length === 0 ? (
                  <p className="text-sm text-stone-400 italic">
                    {division === "Rangers"
                      ? "No Rangers cabin assigned for this session."
                      : "All cabins hidden."}
                  </p>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {slots.map((slot) => (
                      <CabinCard
                        key={`${division}-${slot.number}-${slot.cabin?.id ?? "empty"}`}
                        slot={slot}
                        isAdmin={isAdmin}
                        onOpen={(cabin) => {
                          setSelectedCabin(cabin);
                          setCabinDetailOpen(true);
                        }}
                      />
                    ))}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {selectedCabin && (
        <CabinDetailModal
          open={cabinDetailOpen}
          cabin={selectedCabin}
          cabinGroups={[]}
          year={selectedTerm?.year ?? selectedYear}
          termId={selectedTerm?.id}
          termName={selectedTerm?.name}
          onClose={() => {
            setCabinDetailOpen(false);
            setSelectedCabin(null);
          }}
          onUpdated={() => fetchCabins()}
        />
      )}
    </div>
  );
}

interface CabinCardProps {
  slot: CabinSlot;
  isAdmin: boolean;
  onOpen: (cabin: Cabin) => void;
}

function CabinCard({ slot, isAdmin, onOpen }: CabinCardProps) {
  const { cabin, label } = slot;
  const isUnused = cabin == null;
  const camperCount = cabin?.camperCount ?? 0;

  // Cross-check the cabin's apparent division against its number range.
  // If the cabin number maps to a different division than its CampMinder
  // attribution suggests, the UI should still trust the number range —
  // this is mostly a smoke-test that the presenter is doing its job.
  const numberDivision = cabin ? divisionForCabinNumber(parseCabinNumber(cabin.name)) : null;

  return (
    <Card className={`hover:shadow-md transition-shadow ${isUnused ? "opacity-60" : ""}`}>
      <div className="flex items-start gap-4">
        <div className="w-10 h-10 rounded-xl bg-forest-100 flex items-center justify-center flex-shrink-0">
          <HomeModernIcon className="w-5 h-5 text-forest-600" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="font-semibold text-stone-900 truncate">{label}</h3>
            {isUnused && <Badge label="Unused" color="stone" />}
            {!isUnused && numberDivision == null && label !== "Rangers" && (
              <Badge label="Off-grid" color="amber" />
            )}
          </div>
          <p className="text-xs text-stone-400 mt-0.5">
            {camperCount} camper{camperCount !== 1 ? "s" : ""}
          </p>
          {cabin?.counselors && cabin.counselors.length > 0 && (
            <p className="text-xs text-stone-400 mt-0.5 truncate">
              {cabin.counselors.map((c) => c.firstName).join(", ")}
            </p>
          )}
        </div>
        {isAdmin && cabin && (
          <div className="flex items-center gap-1 flex-shrink-0">
            <button
              onClick={() => onOpen(cabin)}
              className="p-1 rounded text-stone-400 hover:text-forest-600 hover:bg-stone-100 transition-colors"
              title="Assign counselors/campers"
            >
              <UsersIcon className="w-4 h-4" />
            </button>
            <Link
              to={`/admin/cabins?editCabin=${cabin.id}`}
              className="p-1 rounded text-stone-400 hover:text-forest-600 hover:bg-stone-100 transition-colors"
              title="Edit cabin"
            >
              <PencilSquareIcon className="w-3.5 h-3.5" />
            </Link>
          </div>
        )}
      </div>
    </Card>
  );
}
